import type { Data } from '@repo/strapi-types'
import AppLink from '@/components/elementary/AppLink'
import { cn } from '@/lib/styles'
import type { GridCardProps } from '@/types/general'
import type { ServerRenderedEntry } from '@/types/helpers'
import { AppsCard } from './variants/AppsCard'
import { CompetitorsCard } from './variants/CompetitorsCard'
import { FileFormatCard } from './variants/FileFormatCard'
import { IntegrationsCard } from './variants/IntegrationsCard'
import { IntegrationsWithDescriptionCard } from './variants/IntegrationsWithDescriptionCard'

export function Tile(
	props: ServerRenderedEntry<
		GridCardProps & {
			variant: Data.Component<'shared.tile'>['variant']
			className?: string
			asLink?: boolean
			iconClassName?: string
		}
	>,
) {
	const { variant, className, asLink = true, ...tileParams } = props

	const hasLink = !!props.link?.href

	const commonClassName = cn(
		'group focus:outline-none w-full flex rounded-xl border relative border-neutral-light-600 transition-colors duration-200 h-full items-baseline group-data-[state=active]:cursor-default',
		hasLink &&
			'hover:border-brand-salmon focus-visible:border-brand-salmon active:bg-neutral-light-200 group-data-[state=active]:border-brand-salmon',
		asLink && !hasLink && 'bg-neutral-light-300',
		className,
	)

	let variantComponent = null

	switch (variant) {
		case 'fileFormat':
			variantComponent = <FileFormatCard {...tileParams} />
			break
		case 'competitors':
			variantComponent = <CompetitorsCard {...tileParams} />
			break
		case 'integrations':
			variantComponent = <IntegrationsCard {...tileParams} />
			break
		case 'integrationsWithDescription':
			variantComponent = <IntegrationsWithDescriptionCard {...tileParams} />
			break
		case 'apps':
			variantComponent = <AppsCard {...tileParams} />
			break
		default:
			variantComponent = null
	}

	if (asLink && hasLink) {
		return (
			<AppLink
				href={props.link?.href ?? ''}
				openExternalInNewTab={props.link?.newTab ?? false}
				className={commonClassName}
			>
				{variantComponent}
			</AppLink>
		)
	}

	const isDisabled = asLink && !hasLink

	return (
		<div
			{...(isDisabled && { role: 'group', 'aria-disabled': true })}
			className={cn(
				commonClassName,
				isDisabled && 'cursor-default',
				!asLink && 'flex relative flex-row items-center gap-1 justify-center cursor-pointer',
			)}
		>
			{variantComponent}
		</div>
	)
}
